/**
 * CollectorReconciliationPage — Conciliacion del Collector (FiadoSA)
 * Split panel: tabla resumen anual (izq) + detalle drill-down (der)
 */

import { useState } from 'react';
import { RefreshCw, Save, AlertTriangle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { HelpButton } from '@/features/help';
import { Can } from '@/components/auth/Can';
import { ROUTES, UNE_PERM } from '@/constants';
import { useCollectorReconciliation } from '@/features/reconciliation/hooks';
import { CollectorSummaryTable } from '@/features/reconciliation/components/CollectorSummaryTable';
import { CollectorDetailPanel } from '@/features/reconciliation/components/CollectorDetailPanel';
import { RegisterReconciliationModal } from '@/features/reconciliation/components/RegisterReconciliationModal';
import { RECONCILIATION_CONFIG } from '@/types/collector-reconciliation.types';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear - 2021 }, (_, i) => ({
  value: String(currentYear + 1 - i),
  label: String(currentYear + 1 - i),
}));

export function CollectorReconciliationPage() {
  const navigate = useNavigate();
  const {
    year,
    setYear,
    drillDown,
    resumen,
    monthData,
    drillDownItems,
    isLoading,
    hasData,
    isRegistering,
    runReconciliation,
    handleCellClick,
    closeDrillDown,
    register,
  } = useCollectorReconciliation();

  const [showRegisterModal, setShowRegisterModal] = useState(false);

  const handleRegister = (notes: string) => {
    if (!resumen) return;
    register({
      year,
      month: null,
      totals: resumen.totals,
      status:
        resumen.totals.soloCC + resumen.totals.soloCR > 0
          ? 'CON_DISCREPANCIAS'
          : 'LIMPIO',
      ...(notes ? { notes } : {}),
    });
    setShowRegisterModal(false);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header — estilo DB Conciliacion: back + titulo + subtitulo dinamico */}
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => navigate(ROUTES.RECONCILIATION_DASHBOARD)}
            className="mt-1 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Volver"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-foreground">Conciliación Collector</h1>
              <HelpButton pageKey="reconciliation-collector" />
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {RECONCILIATION_CONFIG.description}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select
            options={YEAR_OPTIONS}
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
            className="w-24"
          />
          <Can permission={UNE_PERM.RECONCILIATION_RUN}>
            <Button onClick={runReconciliation} disabled={isLoading}>
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              {hasData ? 'Actualizar' : 'Conciliar'}
            </Button>
            {hasData && (
              <Button variant="outline" onClick={() => setShowRegisterModal(true)}>
                <Save size={16} /> Registrar
              </Button>
            )}
          </Can>
        </div>
      </div>

      {/* Main content */}
      {isLoading && !hasData ? (
        <div className="space-y-4">
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={400} />
        </div>
      ) : !hasData ? (
        <Card>
          <div className="flex flex-col items-center py-16 text-center">
            <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Sin datos de conciliacion</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Selecciona un ano y haz click en &quot;Conciliar&quot; para ejecutar la conciliacion.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid flex-1 grid-cols-1 gap-4 overflow-hidden lg:grid-cols-2">
          {/* Panel izquierdo — Tabla resumen */}
          <Card className="flex flex-col overflow-hidden">
            <CollectorSummaryTable
              monthData={monthData as Record<string, Record<string, unknown>> | null}
              onCellClick={handleCellClick}
              selectedMonth={drillDown?.month ?? null}
              selectedStatus={drillDown?.statusKey ?? null}
            />
          </Card>

          {/* Panel derecho — Detalle */}
          <Card className="flex flex-col overflow-hidden">
            {drillDown ? (
              <CollectorDetailPanel
                selection={drillDown}
                items={drillDownItems as Record<string, unknown>[]}
                onClose={closeDrillDown}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                Selecciona una celda para ver el detalle
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Register modal */}
      {showRegisterModal && resumen && (
        <RegisterReconciliationModal
          isOpen={showRegisterModal}
          onClose={() => setShowRegisterModal(false)}
          onConfirm={handleRegister}
          resumen={resumen as unknown as { totals: Record<string, number> }}
          isRegistering={isRegistering}
        />
      )}
    </div>
  );
}
